import { supabase } from './client';

export interface Client {
  id: string;
  name: string;
  email?: string;
  phone: string;
  notes: string;
  created_at?: string;
  updated_at?: string;
}

export const clientService = {
  // Buscar todos os clientes
  getClients: async (): Promise<Client[]> => {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .order('name');
    
    if (error) {
      console.error("Erro ao buscar clientes:", error.message);
      throw error;
    }
    
    return data || [];
  },
  
  // Buscar cliente por ID
  getClientById: async (id: string): Promise<Client | null> => {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      console.error(`Erro ao buscar cliente ${id}:`, error.message);
      return null;
    }
    
    return data;
  },
  
  // Buscar clientes por filtro (pesquisa por nome ou email)
  searchClients: async (searchTerm: string): Promise<Client[]> => {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
      .order('name');
    
    if (error) {
      console.error("Erro ao pesquisar clientes:", error.message);
      throw error;
    }
    
    return data || [];
  },
  
  // Criar um novo cliente
  createClient: async (client: Omit<Client, 'id' | 'created_at' | 'updated_at'>): Promise<Client> => {
    // Gerar um UUID para o cliente
    const id = crypto.randomUUID();
    
    const { data, error } = await supabase
      .from('clients')
      .insert([{
        id,
        ...client,
        // A nossa versão do Supabase gera automaticamente timestamps
      }])
      .select()
      .single();
    
    if (error) {
      console.error("Erro ao criar cliente:", error.message);
      throw error;
    }
    
    return data;
  },
  
  // Atualizar cliente existente
  updateClient: async (id: string, updates: Partial<Omit<Client, 'id' | 'created_at' | 'updated_at'>>): Promise<Client> => {
    const { data, error } = await supabase
      .from('clients')
      .update({
        ...updates,
        updated_at: new Date().toISOString(), // Atualizar timestamp manualmente
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error(`Erro ao atualizar cliente ${id}:`, error.message);
      throw error;
    }
    
    return data;
  },
  
  // Excluir cliente
  deleteClient: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error(`Erro ao excluir cliente ${id}:`, error.message);
      throw error;
    }
    
    return true;
  },
  
  // Migrar clientes do localStorage para o Supabase
  migrateLocalClients: async (): Promise<boolean> => {
    try {
      // Obter dados do localStorage
      const localData = localStorage.getItem('voah_clients');
      if (!localData) {
        console.log('Nenhum dado de cliente para migrar.');
        return false;
      }
      
      const clients = JSON.parse(localData);
      if (!Array.isArray(clients) || clients.length === 0) {
        console.log('Sem clientes para migrar ou formato inválido.');
        return false;
      }
      
      // Formatar dados para inserção
      const formattedClients = clients.map(client => ({
        id: client.id, // Preservar IDs originais
        name: client.name,
        email: client.email || null,
        phone: client.phone || '',
        notes: client.notes || '',
      }));
      
      // Inserir em lote no Supabase
      const { error } = await supabase
        .from('clients')
        .upsert(formattedClients, { 
          onConflict: 'id',
          ignoreDuplicates: false 
        });
      
      if (error) {
        console.error("Erro ao migrar clientes:", error.message);
        throw error;
      }
      
      console.log(`${formattedClients.length} clientes migrados com sucesso.`);
      return true;
    } catch (error) {
      console.error("Erro durante migração de clientes:", error);
      throw error;
    }
  }
}; 